import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import api, { formatApiError } from "@/services/api";

export default function AdminForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        email: email.trim().toLowerCase(),
      });
      setDone(true);
      toast.success(data?.message || "OK");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erè");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-blue/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-gold/10 blur-3xl" />
      </div>
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <Logo size={56} />
        </div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-2xl">
          <h1 className="font-heading text-2xl font-bold">{t("forgot_password_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("forgot_password_sub")}</p>

          {done ? (
            <div className="mt-6 space-y-4 text-sm text-muted-foreground">
              <p>
                Si kont sa a egziste epi Resend konfigire, tcheke bwat imèl ou
                pou lyen chanje modpas la (valab 1 èdtan).
              </p>
              <Link
                to="/admin/login"
                className="inline-block text-brand-blue hover:underline font-medium"
                data-testid="forgot-back-login"
              >
                ← {t("back_to_login")}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email">{t("email")}</Label>
                <div className="mt-1.5 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@tgs.com"
                    data-testid="forgot-email-input"
                    className="pl-9 h-11"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-btn"
                className="w-full h-11 bg-brand-blue hover:bg-brand-blueHover text-white font-semibold"
              >
                {loading ? t("loading") : t("forgot_password_send")}
              </Button>
              <div className="text-center text-xs">
                <Link to="/admin/login" className="text-muted-foreground hover:text-foreground">
                  ← {t("back_to_login")}
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
